
import React, { useState, useRef, useEffect } from 'react';
import MusicTempo from 'music-tempo';
import InputBase from '@mui/material/InputBase';
import Box from "@mui/material/Box";
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import useUploadContext from "hooks/useUploadContext";
import WaveFrom from 'components/UploadPage/MainContainer/LayersBox/WaveForm';
import BareSetting from 'components/UploadPage/MainContainer/LayersBox/BareSetting';
import VolumeOff from "assets/images/volume-off.png";
import VolumeOn from "assets/images/volume-on.png";
import Lock from "assets/images/lock.png";
import Unlock from "assets/images/unlock.png";
import "./index.scss";

const BaseButton = styled(Button)(() => ({
    height: '100%',
    boxSizing: 'border-box',
    padding: 0,
    margin: 0,
    borderRadius: 0,
    minWidth: 0,
}));

const UploadButton = styled(BaseButton)(() => ({
    width: '100%',
    backgroundColor: '#D9D9D9',
    boxShadow: 'inset 0 0 6px #000000d9',
    '&:hover': {
        backgroundColor: '#D9D9D9',
    },
    '.MuiSvgIcon-root': {
        transform: 'rotate(45deg)',
        fill: '#3A3A3A',
    }
}));

const LayerActionButton = styled(BaseButton)(() => ({
    width: '48%',
    'img': {
        width: '100%',
        height: '100%',
    }
}));

const LayerBox = () => {
    const {
        layers,
        setLayers,
        tempo,
        setTempo,
        totalTime,
        setTotalTime,
        selectedLayer,
        setSelectedLayer,
        mutes,
        setMutes,
        inProcess,
        audioContextRef,
        setLoading
    } = useUploadContext();
    const refArr = useRef([]);
    const [animStates, setAnimStates] = useState([]);
    const [alerts, setAlerts] = useState(Array(5).fill(null));

    const updateLoading = ()=> {
        const loadings = refArr.current.map(ref => ref.wave.loading);
        const isLoading = loadings.some(v => v);
        setLoading(isLoading);
    };


    useEffect(() => {
        const newAnimStates = layers.map((_, i) => {
            const container = refArr.current[i]?.container;
            const content = refArr.current[i]?.content;
            if (container && content) {
                return content.scrollWidth > container.offsetWidth;
            }
            return false;
        });
        setAnimStates(newAnimStates);
    }, [layers, selectedLayer]);

    const handleFileChange = async (e, i) => {
        if (!e.target.files) return;

        const maxFileSizeMB = 200;
        const file = e.target.files[0];
        let newAlerts = [...alerts];
        newAlerts[i] = null;

        if (!file) return
        if (!file.type.startsWith('audio/')) return;
        if (file.size > maxFileSizeMB * 256 * 1024) {
            newAlerts[i] = 'The size of your uploaded file should not be more than 200mb';
            setAlerts(newAlerts);
            return;
        };

        const newLayers = [...layers];

        if (refArr.current[i]?.wave) {
            refArr.current[i]?.wave.updateLoading(true);
        }

        const audioContext = audioContextRef?.current;
        const fileReader = new FileReader();
        setSelectedLayer(i);

        fileReader.onload = () => {
            audioContext.decodeAudioData(fileReader.result).then((audioBuffer) => {
                const audioData = [];
                if (audioBuffer.numberOfChannels === 2) {
                    const channel1Data = audioBuffer.getChannelData(0);
                    const channel2Data = audioBuffer.getChannelData(1);
                    const length = channel1Data.length;
                    for (let i = 0; i < length; i++) {
                        audioData[i] = (channel1Data[i] + channel2Data[i]) / 2;
                    }
                }
                let soundTempo = -1;
                try {
                    const mt = new MusicTempo(audioData);
                    soundTempo = Math.round(mt?.tempo)
                    if (tempo > 0 && soundTempo !== tempo) {
                        newAlerts[i] = 'The Tempo of your uploaded files should be same';
                        setAlerts(newAlerts);
                    };

                    if (tempo <= 0) {
                        setTempo(soundTempo);
                    };
                }
                catch (e) {
                    newAlerts[i] = 'The Tempo of your uploaded file is unrecognizable';
                    setAlerts(newAlerts);
                }

                const fileObject = {
                    blob: new Blob([file], { type: file.type }),
                    length: audioBuffer.duration,
                    created: new Date(),
                    lastModified: file.lastModified,
                    type: file.type,
                    size: file.size,
                    name: file.name.split('.').slice(0, -1).join('.'),
                };

                newLayers[i] = {
                    file: fileObject,
                    title: file.name.split('.').slice(0, -1).join('.'),
                    locked: false,
                    created: new Date(),
                    length: audioBuffer.duration,
                    tempo: soundTempo,
                };

                if (audioBuffer.duration > totalTime) {
                    setTotalTime(audioBuffer.duration);
                }

                setSelectedLayer(null);
                setLayers(newLayers);
            })
            
            .catch((error) => {
                console.error('Error getting audio data:', error);
            });
            ;
        };
        fileReader.readAsArrayBuffer(file);
    };
    const handleMute = (i) => {
        const newMues = [...mutes];
        newMues[i] = !newMues[i];
        setMutes(newMues);
    };
    const handleLock = (i) => {
        const newLayers = [...layers];
        newLayers[i].locked = !newLayers[i].locked;
        setLayers(newLayers);
    };
    const handleChangeTitle = (e, i) => {
        const newLayers = [...layers];
        if (newLayers[i]) {
            newLayers[i].title = e.target.value;
        } else {
            newLayers[i] = {
                title: e.target.value
            };
        }
        setLayers(newLayers);
    };
    const clearAlert = (i) => {
        const newAlerts = [...alerts];
        newAlerts[i] = null;
        setAlerts(newAlerts);
    };

    return (
        <Box className='layer-wrapper'>
            {
                layers.map((layer, i) => (
                    <Box key={i} className="layer-upload-item-box">
                        <Box
                            className="title"
                            ref={(el) => (refArr.current[i] = { ...refArr.current[i], container: el })}
                            onDoubleClick={() => setSelectedLayer(i)}
                        >
                            {selectedLayer === i ?
                                (
                                    <InputBase
                                        value={layer?.title ? layer.title : `layer ${i + 1}`}
                                        className={`input ${animStates[i] ? ' animate' : ''}`}
                                        inputRef={(el) => (refArr.current[i] = { ...refArr.current[i], content: el })}
                                        readOnly={selectedLayer !== i || !inProcess}
                                        onBlur={() => setSelectedLayer(null)}
                                        onChange={(e) => handleChangeTitle(e, i)}
                                    />
                                ) : (
                                    <div
                                        ref={(el) => (refArr.current[i] = { ...refArr.current[i], content: el })}
                                        className={`${animStates[i] ? ' animate' : ''} title-text`}
                                    >
                                        {layer?.title ? layer.title : `layer ${i + 1}`}
                                    </div>
                                )
                            }

                        </Box>
                        <Box className="wave-wrapper">
                            <Box className={`layer-wave ${!!alerts[i]} ? 'error' : ''`}>
                            <WaveFrom
                                updateLoading={updateLoading}
                                file={layer?.file}
                                waveLength={layer?.length}
                                ref={(el) => (refArr.current[i] = { ...refArr.current[i], wave: el })}
                            />
                               
                            </Box>
                            {layer?.file && <BareSetting />}
                            {!!alerts[i] && <div className="alert">
                                {alerts[i]}
                                <span className="ok-button" onClick={() => clearAlert(i)}>
                                    Ok
                                </span>
                            </div>
                            }
                        </Box>
                        <Box className={`layer-buttons ${!inProcess ? 'disabled' : ''}`}>
                            {
                                layer?.file ? (
                                    <>
                                        <LayerActionButton onClick={() => handleMute(i)}>
                                            <img
                                                src={mutes[i] ? VolumeOff : VolumeOn}
                                                alt="Lock Button"
                                            />
                                        </LayerActionButton>
                                        <LayerActionButton onClick={() => handleLock(i)}>
                                            <img
                                                src={layer.locked ? Lock : Unlock}
                                                alt="Lock Button"
                                            />
                                        </LayerActionButton>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            id={`id-${i}`}
                                            type="file"
                                            onChange={(e) => handleFileChange(e, i)}
                                            hidden
                                            accept="audio/*"
                                            disabled={!inProcess}
                                        />
                                        <label htmlFor={`id-${i}`}>
                                            <UploadButton
                                                className="upload-button"
                                                component="span"
                                            >
                                                <AttachFileIcon/>
                                            </UploadButton>
                                        </label>
                                    </>
                                )
                            }
                        </Box>
                    </Box>
                ))
            }
        </Box >
    )
}

export default LayerBox;
